// shared/tooltip.directive.ts
import {
    Directive,
    ElementRef,
    HostListener,
    OnDestroy,
    input,
    Renderer2,
} from '@angular/core';

/**
 * Tooltip flutuante exibido ao passar o mouse sobre o elemento. O balão
 * (`div.custom-tooltip`, estilizado globalmente) é anexado ao `<body>` e
 * **segue o ponteiro do mouse**.
 *
 * Remove o balão no `mouseleave` **e** no `ngOnDestroy` — este último é
 * essencial: sem ele, quando o elemento é destruído com o mouse por cima (ex.:
 * uma tabela que se re-renderiza), o `mouseleave` não dispara e o tooltip
 * ficaria "preso" no body para sempre.
 */
@Directive({
    selector: '[aiTooltip]',
})
export class TooltipDirective implements OnDestroy {
    tooltipText = input<string>('');
    /**
     * @deprecated Mantidos por compatibilidade de API — a posição agora segue o
     * cursor, com ajuste automático para caber na viewport.
     */
    verticalPosition = input<'top' | 'bottom'>('top');
    horizontalPosition = input<'left' | 'right'>('left');

    private tooltipEl?: HTMLElement;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
    ) {}

    @HostListener('mouseenter', ['$event'])
    onMouseEnter(event: MouseEvent) {
        const text = this.tooltipText();
        if (!text) return;

        this.createTooltip(text);
        this.positionAt(event.clientX, event.clientY);
    }

    @HostListener('mousemove', ['$event'])
    onMouseMove(event: MouseEvent) {
        if (this.tooltipEl) {
            this.positionAt(event.clientX, event.clientY);
        }
    }

    @HostListener('mouseleave')
    onMouseLeave() {
        this.removeTooltip();
    }

    ngOnDestroy(): void {
        this.removeTooltip();
    }

    private createTooltip(text: string) {
        this.tooltipEl = this.renderer.createElement('div');
        this.tooltipEl!.className = 'custom-tooltip';
        this.tooltipEl!.innerText = text;

        this.renderer.appendChild(document.body, this.tooltipEl);
    }

    /**
     * Posiciona o balão ao lado do cursor (abaixo/à direita por padrão),
     * espelhando para o outro lado quando não couber na viewport.
     */
    private positionAt(x: number, y: number) {
        if (!this.tooltipEl) return;

        const gap = 14;
        const rect = this.tooltipEl.getBoundingClientRect();

        let left = x + gap;
        if (left + rect.width > window.innerWidth) {
            left = x - rect.width - gap;
        }

        let top = y + gap;
        if (top + rect.height > window.innerHeight) {
            top = y - rect.height - gap;
        }

        this.renderer.setStyle(this.tooltipEl, 'left', `${Math.max(4, left)}px`);
        this.renderer.setStyle(this.tooltipEl, 'top', `${Math.max(4, top)}px`);
    }

    private removeTooltip() {
        if (this.tooltipEl) {
            this.renderer.removeChild(document.body, this.tooltipEl);
            this.tooltipEl = undefined;
        }
    }
}
